import { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, Plus, X } from 'lucide-react';
import type { NewDealConfirmation } from './newDealConfirmation';
import RequiredAtConfirmFields from './RequiredAtConfirmFields';
import { overridesFromAnswers, type ConfirmOverrides } from './requiredAtConfirm';

/**
 * Create Deal, confirmed (nd-037 / nd3-19).
 *
 * Two things made Create Deal a one-click mistake on a reply to a killed
 * deal's thread: the record was held because its deal name was only the
 * subject line ("RE: Loan request"), and it matched the killed deal the desk
 * might rather reopen. One click created "Loan request" — ignoring the
 * Suggested "Hazel Glen Apartments" on the Required Fields panel — and the
 * new deal said nothing about the killed one. Here the person confirms the
 * name (the review decision the hold waits on) and sees the killed
 * deal before anything is created.
 */

export default function ConfirmNewDealDialog({
  confirmation,
  busy,
  error,
  onConfirm,
  onClose,
  onOpenDeal,
}: {
  confirmation: NewDealConfirmation;
  busy: boolean;
  error?: string | null;
  /** The confirmed name when one was asked for, else null; and the answers to
   *  the required-at-intake fields the server named, when it did. */
  onConfirm: (name: string | null, overrides?: ConfirmOverrides) => void;
  onClose: () => void;
  onOpenDeal?: (loanId: string) => void;
}) {
  const { needsName, suggested, subjectName, killed, missing } = confirmation;
  const [name, setName] = useState(suggested ?? '');
  // The four fields required at intake, asked for when the server named any.
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [answerErrors, setAnswerErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const trimmed = name.trim();
  const needsAnswers = Boolean(missing && missing.length > 0);
  const answered = !needsAnswers || missing!.every((gap) => (answers[gap.field] ?? '').trim().length > 0);
  const canCreate = !busy && (!needsName || trimmed.length > 0) && answered;

  const confirm = () => {
    if (!needsAnswers) {
      onConfirm(needsName ? trimmed : null);
      return;
    }
    const { overrides, errors } = overridesFromAnswers(missing!, answers);
    setAnswerErrors(errors);
    if (Object.keys(errors).length > 0) return;
    onConfirm(needsName ? trimmed : null, overrides);
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={() => !busy && onClose()}
    >
      <div
        role="dialog"
        aria-label="Confirm the new deal"
        className="w-full max-w-lg rounded-lg border border-amber-500/20 bg-stone-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-stone-800 px-4 py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-400" />
            <span className="text-[12px] font-medium text-stone-200">Confirm before creating the deal</span>
          </div>
          <button onClick={() => !busy && onClose()} className="text-stone-500 hover:text-stone-300" title="Close">
            <X size={15} />
          </button>
        </div>

        <div className="space-y-4 px-4 py-4">
          {killed.map((m) => (
            <div
              key={m.loanId}
              data-testid="killed-deal-match"
              className="flex items-start gap-2 rounded border border-stone-700 bg-stone-900/60 px-3 py-2 text-[11px] text-stone-300"
            >
              <span className="min-w-0 flex-1">
                This request matches killed deal <span className="font-mono">{m.loanId}</span>
                {m.property ? ` (${m.property})` : ''} — {m.reason || m.basis}. Creating makes a{' '}
                <strong className="text-stone-100">new</strong> deal; it never reopens {m.loanId}. Reopen {m.loanId}{' '}
                instead if this is the same deal coming back.
              </span>
              {onOpenDeal ? (
                <button
                  type="button"
                  onClick={() => onOpenDeal(m.loanId)}
                  aria-label={`Open killed deal ${m.loanId}`}
                  title={`Open ${m.loanId} to compare, or to reopen it`}
                  className="shrink-0 rounded border border-stone-700 p-1 text-stone-400 hover:border-amber-400/40 hover:text-amber-300"
                >
                  <ExternalLink size={12} />
                </button>
              ) : null}
            </div>
          ))}

          {needsName ? (
            <label className="block space-y-1.5">
              <span className="block text-[10px] uppercase tracking-wide text-stone-500">Deal name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                placeholder="The property's name"
                className="w-full rounded border border-stone-700 bg-stone-900 px-2.5 py-1.5 text-[12px] text-stone-100 focus:border-amber-400/60 focus:outline-none"
              />
              <span className="block text-[10px] text-stone-400">
                The engine could not read a name it is confident of
                {subjectName ? <> and fell back to the subject line (“{subjectName}”)</> : null}.{' '}
                {suggested
                  ? <>“{suggested}” is suggested by the record; it becomes the deal's name only when you confirm it here.</>
                  : 'Type the property’s name; it is recorded as your decision.'}
              </span>
            </label>
          ) : null}

          {needsAnswers ? (
            <RequiredAtConfirmFields
              gaps={missing!}
              answers={answers}
              errors={answerErrors}
              disabled={busy}
              onChange={(field, value) => {
                setAnswers((prev) => ({ ...prev, [field]: value }));
                setAnswerErrors((prev) => ({ ...prev, [field]: '' }));
              }}
            />
          ) : null}

          {error ? <p className="text-[11px] text-red-400">{error}</p> : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stone-800 px-4 py-3">
          <button
            onClick={() => !busy && onClose()}
            disabled={busy}
            className="px-3 py-1.5 text-[11px] font-medium text-stone-400 hover:text-stone-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={!canCreate}
            className="btn-primary flex items-center gap-1.5 px-3 py-1.5 text-[11px] disabled:opacity-50"
          >
            <Plus size={12} />
            {busy ? 'Creating…' : needsName ? 'Confirm name and create deal' : 'Create new deal'}
          </button>
        </div>
      </div>
    </div>
  );
}
